<template>
  <div :style="{ padding: '12px', minHeight: '360px' }">
    <a-bread-crumb :crumbs="[{ path: 'tenant', name: 'Tenant' }]" />
    <a-tabs defaultActiveKey="1" :size="size">
      <a-tab-pane key="1">
        <span slot="tab">
          <a-icon type="setting" theme="filled" />Tenant Settings
        </span>
        <a-skeleton active :loading="loading" :title="false" :paragraph="{ rows: 8, width: 800 }">
          <a-form :layout="formLayout" :form="form" :key="componentKey">
            <a-alert style="margin-bottom:5px;" v-if="!isSetupComplete" closable>
              <h3 slot="message">Complete your tenant setup</h3>
              <div slot="description">
                <a-progress :percent="isSetupPercentage" />
                <p class="lead">Please update following options.</p>
                <ul>
                  <li v-if="registration_redirect_url === ''">Redirect URL in Registration Options</li>
                  <li v-if="login_url === '#'">Login callback URL in Login and Logout URLs</li>
                  <li v-if="logout_url === '#'">Logout redirect URL in Login and Logout URLs</li>
                </ul>
              </div>
            </a-alert>
            <a-collapse accordion defaultActiveKey="1">
              <a-collapse-panel header="General" key="1">
                <a-tenant-general
                  :formLayout="formLayout"
                  :size="size"
                  :tenant="tenant"
                  :domain="primary_domain"
                />
              </a-collapse-panel>
              <a-collapse-panel header="Branding Options" key="2">
                <a-branding-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Password Policies" key="3">
                <a-password-policies :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Password Options" key="4">
                <a-password-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Registration Options" key="5">
                <a-registration-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Login and Logout URLs" key="6">
                <a-login-logout-options
                  :formLayout="formLayout"
                  :size="size"
                  :data="tenant"
                  :form="form"
                />
              </a-collapse-panel>
              <a-collapse-panel header="JWT Token Options" key="7">
                <a-jwt-token-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Session Options" key="8">
                <a-session-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Multi-factor Options" key="9">
                <a-mfa-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Passwordless Options" key="10">
                <a-passwordless-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Token Endpoint Options" key="11">
                <a-token-endpoint-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Authorization Endpoint Options" key="12">
                <a-authorize-endpoint-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
              <a-collapse-panel header="Captcha Options" key="13">
                <a-recaptcha-options :formLayout="formLayout" :size="size" :data="tenant" />
              </a-collapse-panel>
            </a-collapse>
            <a-form-item
              :wrapper-col="buttonItemLayout.wrapperCol"
              style="margin-top:0.5em;float: left;"
            >
              <a-button
                shape="round"
                type="primary"
                size="large"
                @click="handleSubmit"
              >Update Details</a-button>
            </a-form-item>
          </a-form>
        </a-skeleton>
      </a-tab-pane>
      <a-tab-pane key="2">
        <span slot="tab">
          <a-icon type="smile" theme="filled" />Management Users
        </span>
        <a-user-list :is_manage_user="true" />
      </a-tab-pane>
      <a-tab-pane key="3" v-if="$role(['tenant:owner']) && tenant.subscription_id">
        <span slot="tab">
          <a-icon type="global" />Domains
        </span>
        <a-domains-tab />
      </a-tab-pane>
      <a-tab-pane key="4" v-if="$role(['tenant:owner'])">
        <span slot="tab">
          <a-icon type="warning" theme="filled" />Danger Zone
        </span>
        <a-tenant-delete />
      </a-tab-pane>
      <a-button
        slot="tabBarExtraContent"
        type="danger"
        :href="'https://developer.axioms.io/docs/getting-started/tenant/pages-endpoints/?domain='+primary_domain.domain"
        target="_blank"
      >Tenant Urls <a-icon type="arrow-right" /></a-button>
    </a-tabs>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ABrandingOptions from "@/components/FormSet/ABrandingOptions";
import ARecaptchaOptions from "@/components/FormSet/ARecaptchaOptions";
import APasswordPolicies from "@/components/FormSet/APasswordPolicies";
import APasswordOptions from "@/components/FormSet/APasswordOptions";
import ATenantGeneral from "@/components/FormSet/ATenantGeneral";
import ATenantDelete from "@/components/FormSet/ATenantDelete";
import APasswordlessOptions from "@/components/FormSet/APasswordlessOptions";
import ATokenEndpointOptions from "@/components/FormSet/ATokenEndpointOptions";
import AAuthorizeEndpointOptions from "@/components/FormSet/AAuthorizeEndpointOptions";
import ARegistrationOptions from "@/components/FormSet/ARegistrationOptions";
import AJwtTokenOptions from "@/components/FormSet/AJwtTokenOptions";
import ASessionOptions from "@/components/FormSet/ASessionOptions";
import AMfaOptions from "@/components/FormSet/AMfaOptions";
import ADomainsTab from "@/components/Tab/ADomainsTab";
import ALoginLogoutOptions from "@/components/FormSet/ALoginLogoutOptions";
import { formLayoutMixin } from "@/mixins/formLayout";
import AUserList from "@/components/List/AUserList";

export default {
  data() {
    return {
      componentKey: 10,
      loading: true,
      size: "large",
      formLayout: "horizontal",
      form: this.$form.createForm(this),
      colors: [
        "primary",
        "info",
        "danger",
        "success",
        "warning",
        "secondry",
        "background"
      ]
    };
  },
  mixins: [formLayoutMixin],

  computed: {
    ...mapGetters("core", [
      "login_url",
      "logout_url",
      "registration_redirect_url",
      "primary_domain"
    ]),

    isSetupComplete() {
      return this.login_url === "#" ||
        this.logout_url === "#" ||
        this.registration_redirect_url === ""
        ? false
        : true;
    },
    isSetupPercentage() {
      var complete = 100;
      if (this.login_url === "#") {
        complete = complete - 5;
      }
      if (this.logout_url === "#") {
        complete = complete - 5;
      }
      if (this.registration_redirect_url === "") {
        complete = complete - 5;
      }
      return complete;
    },
    tenant() {
      return this.$store.state.core.tenants_item;
    }
  },

  components: {
    ABrandingOptions,
    APasswordPolicies,
    ATenantGeneral,
    ATenantDelete,
    ARegistrationOptions,
    AJwtTokenOptions,
    ASessionOptions,
    AMfaOptions,
    AUserList,
    ALoginLogoutOptions,
    APasswordlessOptions,
    ADomainsTab,
    ATokenEndpointOptions,
    AAuthorizeEndpointOptions,
    APasswordOptions,
    ARecaptchaOptions
  },

  methods: {
    handleSubmit() {
      const form = this.form;
      form.validateFields(async (err, values) => {
        if (!err) {
          try {
            await this.$store.dispatch("core/patchItem", {
              endpoint: "tenants",
              uuid: this.tenant.uuid,
              data: values
            });
            await this.forceRerender();
            await this.$notification.open({
              message: "Updated!",
              description: "Tenant settings updated",
              icon: (
                <a-icon
                  type="check-circle"
                  theme="filled"
                  style="color: green"
                />
              )
            });
          } catch (error) {
            console.error(error);
          }
        } else {
          console.error("Error! Check submitted values: ", values);
        }
      });
    },
    success() {
      this.$message.success("Copied.", 5);
    },
    forceRerender() {
      this.componentKey += 1;
    }
  },

  beforeCreate: async function() {
    this.form = this.$form.createForm(this);
    try {
      await this.$store.dispatch("core/getItem", {
        endpoint: "tenants",
        uuid: this.$auth.session.org
      });
      this.loading = false;
    } catch (error) {
      console.error(error);
      this.loading = false;
    }
  }
};
</script>
